import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './presenters/components/header';
import { Footer } from './presenters/components/footer';
import { router } from './Routes';
import { createContext, useEffect, useState } from 'react';
import { loadStaticValues } from './insfrastructure/services/static_values';
import { retrieveCandidate } from './insfrastructure/services/candidates';
import { parseJwt } from './insfrastructure/utils/JWTParser';
import './styles/main.sass';
import { retrieveCompanyManager } from './insfrastructure/services/companies';
import { analytics, logger } from './insfrastructure/services/analytics';
import { useTokenRefresh } from './insfrastructure/hooks/useTokenRefresh';
import { setAuthToken, setRefreshToken, clearAllTokens, getAuthToken } from './insfrastructure/services/token-manager';
import { isNetworkError, isSessionExpiredError } from './insfrastructure/utils/errors';
import { refreshAccessToken, isTokenExpiringSoon } from './insfrastructure/services/auth';
import { setupAxiosInterceptors } from './insfrastructure/services/axios-client';
import PrivacyConsent from './presenters/components/privacy-consent';
import { MarketingPopup } from './presenters/components/marketing-popup';
import { WhatsAppButton } from './presenters/components/whatsapp-button';
import { useMarketingPopup } from './insfrastructure/hooks/useMarketingPopup';
import { AUTH_CONSTANTS } from './insfrastructure/utils/auth-constants';
import { isProtectedRoute, isJobDetailRoute } from './insfrastructure/utils/route-utils';

setupAxiosInterceptors();

export const UserdataContext = createContext<any>(null);

export default function App() {
  const [staticValues, setStaticValues] = useState<any>(null)
  const [userData, setUserData] = useState<any>(null)
  const location = useLocation()
  const navigate = useNavigate()
  
  // Hook para renovação automática de tokens
  useTokenRefresh()
  
  // Hook para gerenciar popup de marketing
  const { popup, isOpen, closePopup } = useMarketingPopup()

  useEffect(() => {
    // Medir performance após carregamento
    setTimeout(() => {
      analytics.measurePerformance();
    }, 1000);
    
    loadStaticValues()
      .then(staticValues => {
        localStorage.setItem('static_values', JSON.stringify(staticValues));
        setStaticValues(staticValues);
      })
      .catch(error => {
        logger.error('Erro ao carregar static_values', error);
      });    
  }, [])

  useEffect(() => {
    checkLogin();
    
    // Rastrear mudanças de página
    analytics.trackPageView(location.pathname + location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const checkLogin = async () => {
    let token = getAuthToken();

    // Se não há token, fazer logout
    if (!token) {
      onLogout();
      return;
    }

    let parsedJWT = parseJwt(token);

    if (!parsedJWT) {
      onLogout();
      return;
    };

    if(!parsedJWT?.user_id || (!parsedJWT?.manager_id && !parsedJWT?.candidate_id)) {
      if (parsedJWT?.user_id && !parsedJWT?.candidate_id && !parsedJWT?.manager_id) {
        analytics.addBreadcrumb('IDs de perfil ausentes no JWT', 'auth', 'error');
        analytics.trackError(
          new Error('user_identifiers_missing_in_token'),
          'checkLogin_missing_user_identifiers'
        );
      }
      onLogout();
      return;
    };

    // Se o access token já expirou, tentar renovar antes de chamar endpoints protegidos
    if (isTokenExpiringSoon(token, 0)) {
      try {
        const newTokens = await refreshAccessToken();
        const rememberMe = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME) === 'true';

        setAuthToken(newTokens.access, rememberMe);
        if (newTokens.refresh) {
          setRefreshToken(newTokens.refresh, rememberMe);
        }

        token = newTokens.access;
        const refreshedJWT = parseJwt(token);
        if (refreshedJWT) {
          parsedJWT = refreshedJWT;
        }
      } catch (error) {
        if (isSessionExpiredError(error)) {
          onLogout();
          return;
        }

        if (isNetworkError(error)) {
          logger.error('Erro de rede ao renovar token no checkLogin', error as Error, {
            isNetworkError: true,
            preserveTokens: true
          });
          return;
        }

        logger.error('Erro ao renovar token no checkLogin', error as Error);
        onLogout();
        return;
      }
    }

    // Configurar token globalmente (já está no storage correto)
    setAuthToken(token);

    // Garantir IDs básicos no localStorage para requisições que dependem deles
    let syncedIds = false;
    if (parsedJWT.user_id && !localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.USER_ID)) {
      localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.USER_ID, String(parsedJWT.user_id));
      syncedIds = true;
    }
    if (parsedJWT.candidate_id && !localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.CANDIDATE_ID)) {
      localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.CANDIDATE_ID, String(parsedJWT.candidate_id));
      syncedIds = true;
    }
    if (parsedJWT.manager_id && !localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.MANAGER_ID)) {
      localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.MANAGER_ID, String(parsedJWT.manager_id));
      syncedIds = true;
    }
    if (syncedIds) {
      analytics.addBreadcrumb('IDs sincronizados do JWT para storage', 'auth', 'info');
    }

    if (parsedJWT.legacy_password_login === true && !['/reset-password', '/validate-token'].includes(location.pathname)) {
      router.navigate('/reset-password');

      return;
    };

    try {
      let userdata: any = {};

      if (parsedJWT.candidate_id) {
        userdata = await retrieveCandidate(String(parsedJWT.candidate_id));
        userdata.usertype = parsedJWT.profiles[0];
      } else {
        if (!parsedJWT.manager_id) {
          analytics.addBreadcrumb('Manager ID ausente no JWT', 'auth', 'error');
          analytics.trackError(new Error('manager_id_missing_in_token'), 'checkLogin_missing_manager_id');
          onLogout();
          return;
        }
        userdata = await retrieveCompanyManager(
          parsedJWT.manager_id ? String(parsedJWT.manager_id) : null
        );
        userdata.usertype = parsedJWT.profiles[0];
      };
      
      setUserData(userdata);
      
      // Rastrear usuário logado
      analytics.setUser(parsedJWT.user_id, userdata.usertype);
    } catch (error) {
      logger.error('Erro ao verificar login', error as Error);
      analytics.trackError(error as Error, 'checkLogin');
      onLogout();
    };
  }

  const onLogout = () => {
    clearAllTokens();
    setUserData(null);
    if (isProtectedRoute(location.pathname)) {
      // Armazenar a rota de origem para exibir mensagem específica
      if (isJobDetailRoute(location.pathname)) {
        sessionStorage.setItem('redirect_from_offers', 'true');
      }
      navigate('/login');  
    };
  }
  
  return (
    <UserdataContext.Provider value={{userData, setUserData}}>
      {staticValues &&
      <div className="page">
        <Header />
        <div className='page-content'>
          <Outlet />
        </div>
        <Footer/>
        <WhatsAppButton />
        <PrivacyConsent />
        {popup && <MarketingPopup popup={popup} isOpen={isOpen} onClose={closePopup} />}
      </div>}
    </UserdataContext.Provider>
  )
}
